/* @jsx h */
import { defineComponent, h, ref, computed, PropType } from "vue";
import { fuzzyMatch } from "../../../utils/fuzzyMatch";
import { useLocalization } from "@lemmonhq/uikit";

interface SearchSuggestion {
  type: "restaurant" | "cuisine" | "feature";
  label: string;
  icon?: string;
}

export const SearchBar = defineComponent({
  props: {
    placeholder: {
      type: String,
      default: "",
    },
    restaurants: {
      type: Array as PropType<
        { name: { en: string | null }; category: Record<string, any> }[]
      >,
      default: () => [],
    },
  },
  emits: ["search"],
  setup(props, { emit }) {
    const { t } = useLocalization();
    const searchQuery = ref("");
    const isFocused = ref(false);

    const suggestions = computed((): SearchSuggestion[] => {
      const q = searchQuery.value.toLowerCase().trim();
      if (!q || q.length < 2) return [];

      const results: SearchSuggestion[] = [];
      const seen = new Set<string>();

      // Match restaurant names
      for (const r of props.restaurants) {
        if (results.length >= 5) break;
        const name = r.name.en;
        if (name && fuzzyMatch(name, q) && !seen.has(name)) {
          seen.add(name);
          results.push({ type: "restaurant", label: name, icon: "restaurant" });
        }
      }

      // Match cuisine categories
      for (const r of props.restaurants) {
        if (results.length >= 8) break;
        for (const cat of Object.keys(r.category || {})) {
          if (fuzzyMatch(cat, q) && !seen.has(cat)) {
            seen.add(cat);
            results.push({ type: "cuisine", label: cat, icon: "local_dining" });
          }
        }
      }

      return results.slice(0, 6);
    });

    const showDropdown = computed(
      () => isFocused.value && suggestions.value.length > 0,
    );

    const handleInput = (e: Event) => {
      if (e.target) {
        searchQuery.value = (e.target as HTMLInputElement).value;
        emit("search", searchQuery.value);
      }
    };

    const handleKeypress = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        isFocused.value = false;
        emit("search", searchQuery.value);
      }
    };

    const selectSuggestion = (suggestion: SearchSuggestion) => {
      searchQuery.value = suggestion.label;
      isFocused.value = false;
      emit("search", suggestion.label);
    };

    return () => (
      <div class="max-w-3xl mx-auto">
        <div class="relative">
          <div class="relative">
            <span
              class="material-symbols-outlined absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            >
              search
            </span>
            <input
              type="text"
              value={searchQuery.value}
              onInput={handleInput}
              onKeypress={handleKeypress}
              onFocus={() => (isFocused.value = true)}
              onBlur={() => setTimeout(() => (isFocused.value = false), 200)}
              placeholder={
                props.placeholder || t("customer.home.search_restaurants")
              }
              aria-label={t("customer.home.search_restaurants")}
              aria-expanded={showDropdown.value}
              aria-autocomplete="list"
              role="combobox"
              class="w-full pl-8 pr-16 sm:pr-24 py-4 text-base sm:text-lg bg-transparent border-none outline-none placeholder-gray-400 font-light"
            />
            <button
              type="button"
              onClick={() => emit("search", searchQuery.value)}
              class="absolute right-0 top-1/2 transform -translate-y-1/2 px-3 sm:px-6 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              style="transition: color 200ms ease;"
            >
              <span class="hidden sm:inline">
                {t("customer.home.search")} →
              </span>
              <span
                class="sm:hidden material-symbols-outlined text-lg leading-none"
                aria-hidden="true"
              >
                arrow_forward
              </span>
            </button>
          </div>

          {/* Autocomplete dropdown */}
          {showDropdown.value && (
            <div
              class="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-30"
              role="listbox"
            >
              {suggestions.value.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  role="option"
                  class="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50"
                  style="transition: background-color 150ms ease;"
                  onMousedown={() => selectSuggestion(s)}
                >
                  <span
                    class="material-symbols-outlined text-base text-gray-400"
                    aria-hidden="true"
                  >
                    {s.icon}
                  </span>
                  <span class="font-light text-gray-700">{s.label}</span>
                  <span class="text-xs text-gray-400 ml-auto capitalize">
                    {t(`customer.home.suggestion_${s.type}`)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
});
